import { generateBillHtml } from "../../printConvertion/DocumentConvert";
import { HMS_REPORT_TEMPLATE_TYPE } from "../../../core/constants/erpConstants";
import { fetchQuotationById, getQuotationPDFObject } from "../ERPActions/erpActions";
import helperServices from "../../../shared/helper/helper";

/**
 * Builds standard Invoice Report DTO expected by DocumentConvert / Adapters for quotations.
 */
export const buildQuotationReportDTO = ({
  quotationData = {},
  patientInfo = null,
  patientName = "",
  org = null,
  masterLocationDetails = [],
}) => {
  const masterList = Array.isArray(masterLocationDetails)
    ? masterLocationDetails
    : masterLocationDetails
    ? [masterLocationDetails]
    : [];

  const quotationLocationId = quotationData?.location ?? quotationData?.locationId;
  const matchedLoc =
    masterList.find((location) => {
      const locationIds = location?.locationDetails?.erpLocationDetails
        ?.map((erpLocation) => erpLocation?.locationid)
        .flat()
        .filter((locationId) => locationId !== null && locationId !== undefined) || [];

      return locationIds.some(
        (locationId) => String(locationId) === String(quotationLocationId)
      );
    }) ||
    masterList.find((l) => l?.locationKey && l.locationKey !== "all") ||
    masterList[0] ||
    null;

  const erpLoc = matchedLoc?.locationDetails?.erpLocationDetails?.[0] || null;

  const firmInfo = {
    FIRM_NAME:
      org?.hospitalName ||
      org?.orgName ||
      org?.name ||
      org?.displayName ||
      erpLoc?.name ||
      "Sri Vedamaayu Ayurveda Hospital and Wellness Centre",
    FIRM_ADDRESS: erpLoc?.address || org?.address || "",
    FIRM_CITY: org?.city || "",
    FIRM_PINCODE: org?.pincode || "",
    FIRM_PHONE: erpLoc?.phone || org?.phone || "",
    FIRM_MOBILE: erpLoc?.phone || org?.phone || "",
    FIRM_EMAIL: org?.email || "",
    FIRM_TIN: org?.tin || org?.gstin || "",
    FIRM_CIN: org?.cin || "",
    FIRM_STATECODE: org?.stateCode || "",
    FIRM_LOCATIONNAME: matchedLoc?.displayName || erpLoc?.name || "",
  };

  const pat = quotationData.patientInfo || patientInfo || {};
  const patientAddress =
    pat.address ||
    pat.addressLine1 ||
    pat.address1 ||
    quotationData.patientAddress ||
    quotationData.address ||
    "";
  const loggedInUserName = helperServices.getLoggedUserName();
  const generatedBy =
    quotationData.userDisplayName ||
    quotationData.generatedBy ||
    quotationData.people?.displayName ||
    (typeof loggedInUserName === "string" ? loggedInUserName : "") ||
    quotationData.person ||
    quotationData.salesPerson ||
    "";
  const effectivePatientName =
    patientName ||
    quotationData.customerName ||
    pat.fullName ||
    (pat.fname ? `${pat.fname} ${pat.lname || ""}`.trim() : "") ||
    "";

  const customerDetail = {
    patientName: effectivePatientName,
    age: pat.age ?? "",
    phono: quotationData.patientPhone || pat.phno || pat.phone || "",
    address: patientAddress,
    patientType: pat.patientType || "OutPatient",
    section: "QUOTATION",
    doctorName:
      pat.doctorName ||
      quotationData.salesPerson ||
      quotationData.person ||
      "",
    roomName: "",
    regNumber:
      pat.ipopnumber ||
      pat.main ||
      pat.id ||
      quotationData.customerId ||
      "",
    regNo:
      pat.ipopnumber ||
      pat.main ||
      pat.id ||
      quotationData.customerId ||
      "",
    ipNumber: pat.visit?.ipopnumber || pat.ipopnumber || "",
    billNumber:
      quotationData.billNumber ||
      (quotationData.quoteId ? `QT-${quotationData.quoteId}` : ""),
    billDate: quotationData.dateNew || quotationData.timestamp || new Date(),
    doa: pat.admissionTime || quotationData.doa || "",
    dod: pat.dischargeTime || quotationData.dod || "",
    dep: "",
    plan: "",
    insurancePolicyNumber: "",
    insuranceCardNumber: "",
  };

  const lines = quotationData.lines || [];
  const billingItem = lines.map((line, idx) => {
    const charges = Number(
      line.priceSell ?? line.price ?? line.charges ?? 0
    );
    const units = Number(line.units ?? line.quantity ?? 1);
    const gross = charges * units;
    const lineDiscount = Number(line.discount ?? line.discountCharges ?? 0);
    const netAmount = Math.max(0, gross - lineDiscount);

    return {
      slNo: idx + 1,
      serviceName:
        line.name ||
        line.productName ||
        line.product ||
        line.description ||
        "Service",
      charges: charges,
      units: units,
      taxRate: Number(line.taxRate ?? 0),
      gross: gross,
      discount: lineDiscount,
      netAmount: netAmount,
    };
  });

  const total = Number(quotationData.totalAmount || 0);
  const discount = Number(quotationData.discountRs || 0);
  const billAmount = Math.max(0, total - discount);

  const billSummary = {
    total: total,
    discount: discount,
    billAmount: billAmount,
    advancePayment: 0,
    paidAmount: 0,
    receivedAmount: 0,
    payable: billAmount,
    roundOff: Number(quotationData.roundOff || 0),
    tax: 0,
    amountWords: "",
    billAmountInWords: "",
    generatedBy,
    paymentList: [],
  };

  return {
    reportName: "QUOTATION",
    narration: quotationData.narration || "",
    narration1: quotationData.narration1 || "",
    firmInfo,
    customerDetail,
    billingItem,
    billSummary,
  };
};

/**
 * Generates the quotation print HTML string.
 */
export const generateQuotationHtml = async (
  rawQuotation,
  { patientInfo, patientName, org, masterLocationDetails } = {}
) => {
  let quote = { ...rawQuotation };

  if (quote.quoteId) {
    const reportDtos = await getQuotationPDFObject(quote.quoteId);
    if (reportDtos.length > 0) {
      const orgCode = org?.orgCode || localStorage.getItem("orgCode") || "vedamaayu";
      return generateBillHtml(
        reportDtos,
        HMS_REPORT_TEMPLATE_TYPE.SINGLE_INVOICE,
        orgCode
      );
    }
  }

  // Fetch full details if lines are empty
  if (!quote.lines || quote.lines.length === 0) {
    if (quote.quoteId) {
      try {
        const fullQuote = await fetchQuotationById(quote.quoteId);
        if (fullQuote && fullQuote.lines && fullQuote.lines.length > 0) {
          quote = { ...quote, ...fullQuote };
        }
      } catch (e) {
        console.warn("Could not fetch full quotation details:", e);
      }
    }
  }

  const dto = buildQuotationReportDTO({
    quotationData: quote,
    patientInfo,
    patientName,
    org,
    masterLocationDetails,
  });

  const orgCode = org?.orgCode || localStorage.getItem("orgCode") || "vedamaayu";
  const html = await generateBillHtml(
    [dto],
    HMS_REPORT_TEMPLATE_TYPE.SINGLE_INVOICE,
    orgCode
  );

  return html;
};
